import { useQuery } from "@apollo/client";
import { GET_INTERNAL_MEETING, GET_TRANSCRIPTS } from "apollo";
import { LoadingLayout, PageLayout, Separator, Text } from "components";
import {
  ArchiveIcon,
  HourglassSimpleLowIcon,
  MapPinLineIcon,
  UserCircleIcon,
} from "components/icons";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import { T_InternalMeeting } from "types";
import { ItemLabel, MeetingLabel, TranscriptCard } from "../components";
import { formatTimeToAM, extractMeetingLocation } from "utils/helpers";
import { MeetingGridStyle, ScreenContainerStyle } from "../styles";

export const InternalMeetingScreen = () => {
  // get meeting id from url params
  const { meetingId } = useParams<{ meetingId: string }>();

  const { data: meetingData, loading } = useQuery<{
    internalMeeting: T_InternalMeeting;
  }>(GET_INTERNAL_MEETING, {
    variables: { meetingId },
  });

  const { data: transcriptData, loading: transcriptLoading } = useQuery<{
    transcripts: any[];
  }>(GET_TRANSCRIPTS, {
    variables: { meetingId: meetingId },
  });

  const meeting = meetingData?.internalMeeting;

  if ((!meeting || !meetingId) && !loading) {
    return (
      <div className={ScreenContainerStyle}>
        <Text.H2>This meeting is missing. Is the url is correct?</Text.H2>
      </div>
    );
  }

  if (loading || transcriptLoading) {
    return <LoadingLayout />;
  }

  return (
    <PageLayout title={`${meeting?.eventTitle || "Meeting"} | Multiplayer`}>
      <div className={ScreenContainerStyle}>
        <Text.H2>
          {meeting?.eventTitle || "Calendar event name not identified"}
        </Text.H2>

        <Separator.Horizontal spacing={5} />

        <div className={MeetingGridStyle}>
          <ItemLabel
            Icon={HourglassSimpleLowIcon}
            label="Time and Date"
            value={
              (meeting?.start
                ? formatTimeToAM(new Date(meeting?.start)).split(" ")[0]
                : "") +
              " - " +
              (meeting?.end ? formatTimeToAM(new Date(meeting.end)) : "/")
            }
            valueExtra={
              meeting?.start
                ? format(new Date(meeting?.start), "EEEE, MMMM d")
                : "/"
            }
          />

          <Separator.Vertical spacing={10} />

          <ItemLabel
            Icon={MapPinLineIcon}
            label="Location"
            value={extractMeetingLocation(meeting?.location || "") || "?"}
            valueExtra={meeting?.location || ""}
          />
        </div>

        <Separator.Horizontal spacing={2.5} />
        <MeetingLabel Icon={UserCircleIcon} label="Attendants" />
        <Separator.Horizontal spacing={1} />
        {meeting?.attendees?.map((a) => (
          <Text.BodySM key={a.email} color="seconday">
            {a.displayName || a.email}
          </Text.BodySM>
        ))}

        <Separator.Horizontal spacing={4.5} />
        <MeetingLabel Icon={ArchiveIcon} label="Transcript" />
        <Separator.Horizontal spacing={1} />
        {transcriptData?.transcripts.map((t) => (
          <TranscriptCard
            key={t._id}
            title={`${format(new Date(t.created), "EEEE, MMMM d")}`}
            subtitle={`${formatTimeToAM(new Date(t.meetingDate))}`}
            transcript={t.detailedSummary}
            id={t._id}
          />
        ))}
      </div>
    </PageLayout>
  );
};
